import {Component, computed, inject, input, output, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {LicenceServerSummary} from '../../../../../../../api';
import {ElciaButton} from '../../../../../../components/buttons/button/button';
import {ElciaIcon} from '../../../../../../components/icon/icon';

interface ProductRow {
  id: number;
  name: string;
  reference?: string | null;
  startDate?: Date | null;
  endDate?: Date | null;
  isActive: boolean;
  features: string[];
}

@Component({
  selector: 'elcia-licence-server-item',
  imports: [TranslatePipe, ElciaButton, ElciaIcon],
  templateUrl: './licence-server-item.html',
  host: {class: 'flex flex-col rounded-xl border border-primary-50 overflow-hidden'},
})
export class LicenceServerItem {
  private readonly translate = inject(TranslateService);

  server = input.required<LicenceServerSummary>();
  isGeneratingCredentials = input<boolean>(false);
  onEdit = output<LicenceServerSummary>();
  onDelete = output<LicenceServerSummary>();
  onGenerateCredentials = output<LicenceServerSummary>();

  // Recalcule les noms localisés au changement de langue.
  private readonly langChange = toSignal(this.translate.onLangChange, {initialValue: null});

  protected readonly licences = computed<ProductRow[]>(() => {
    this.langChange();
    return this.server().products.licences.map(licence => ({
      id: licence.id,
      name: this.localize(licence.nameFr, licence.nameNL, licence.nameEn),
      reference: licence.reference,
      startDate: licence.startDate,
      endDate: licence.endDate,
      isActive: licence.isActive,
      features: licence.features.map(feature => this.localize(feature.nameFr, feature.nameNl, feature.nameEn)).sort(),
    }));
  });

  protected readonly plugins = computed<ProductRow[]>(() => {
    this.langChange();
    return this.server().products.plugins.map(plugin => ({
      id: plugin.id,
      name: this.localize(plugin.nameFr, plugin.nameNL, plugin.nameEn),
      startDate: plugin.startDate,
      endDate: plugin.endDate,
      isActive: plugin.isActive,
      features: [],
    }));
  });

  protected readonly hasProducts = computed(() => this.licences().length > 0 || this.plugins().length > 0);

  // IDs des licences dont la liste de features est dépliée (fermée par défaut).
  private readonly expandedFeatures = signal<ReadonlySet<number>>(new Set());

  protected isFeaturesExpanded(licenceId: number): boolean {
    return this.expandedFeatures().has(licenceId);
  }

  protected toggleFeatures(licenceId: number): void {
    this.expandedFeatures.update(current => {
      const next = new Set(current);
      next.has(licenceId) ? next.delete(licenceId) : next.add(licenceId);
      return next;
    });
  }

  protected generateCredentials(): void {
    this.onGenerateCredentials.emit(this.server());
  }

  protected edit(): void {
    this.onEdit.emit(this.server());
  }

  protected delete(): void {
    this.onDelete.emit(this.server());
  }

  private localize(fr: string, nl: string, en: string): string {
    const lang = this.translate.getCurrentLang() ?? '';
    if (lang.startsWith('fr')) return fr;
    if (lang.startsWith('nl')) return nl;
    return en;
  }
}
